import React from 'react';
import { PasswordInput } from '../../components/PasswordInput';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { colors, fonts, radii, shadows } from '../../theme/tokens';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { httpTismsApi } from '../../api/httpTismsApi';
import { toApiError } from '../../redux/_common/apiClient';
import { authLoginReset, authLoginChanged } from '../../redux/auth/auth.action';
import { sessionReset } from '../../redux/session/session.action';
import { selectMustChangePassword, selectAuthUser } from '../../redux/auth/auth.selectors';

/* Change password.
 *
 * Two entrances, one screen:
 *   - forced, when the server says must_change_password (a fresh account, or
 *     an admin reset). The shell renders this instead of the app, and there is
 *     no way past it — the API refuses every other endpoint anyway.
 *   - voluntary, from the top bar.
 *
 * Changing a password kills every token the account holds, including the one
 * making the request, so this always ends at the sign-in page. That is the
 * point: a password change signs out the sessions somebody else may be
 * holding. */

/**
 * The rules worth previewing as you type. The list of banned values (leaked
 * passwords, the product's own name) lives ONLY on the server: duplicating it
 * here would put those exact strings back into the shipped bundle, which is
 * the bug this whole change exists to undo. A password that trips the server
 * list comes back as a 422 naming it.
 */
const problems = (p: string, email: string): string[] => {
  const out: string[] = [];
  if (p.length < 6) out.push('At least 6 characters');
  const local = email.includes('@') ? email.slice(0, email.indexOf('@')) : '';
  if (local && p.toLowerCase().includes(local.toLowerCase())) out.push('Must not contain your username');
  return out;
};

export const ChangePasswordPage: React.FC = () => {
  const forced = useSelector(selectMustChangePassword);
  const user = useSelector(selectAuthUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [current, setCurrent] = React.useState('');
  const [next, setNext] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [done, setDone] = React.useState(false);

  const rules = problems(next, user?.id ? String(user.id) : '');
  const emailRules = problems(next, '');
  const mismatch = confirm !== '' && confirm !== next;
  const canSubmit = current !== '' && next !== '' && emailRules.length === 0 && !mismatch && !busy;

  const submit = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await httpTismsApi.changePassword(current, next);
      setDone(true);
      // The token used for that call is already dead server-side; drop it here
      // rather than letting the next request 401 on its own.
      window.setTimeout(() => {
        dispatch(authLoginReset());
        dispatch(sessionReset());
        dispatch(authLoginChanged());
        navigate('/', { replace: true });
      }, 2200);
    } catch (err) {
      setError(toApiError(err).message);
      setBusy(false);
    }
  };

  const input: React.CSSProperties = {
    width: '100%',
    padding: '11px 12px',
    borderRadius: radii.input,
    border: `1px solid ${colors.borderWarm}`,
    background: '#fff',
    fontSize: 13.5,
    fontFamily: fonts.body,
    marginTop: 6,
  };
  const label: React.CSSProperties = { fontSize: 12, color: colors.muted, marginTop: 14, display: 'block' };

  const body = (
    <Card style={{ maxWidth: 460, width: '100%', padding: 30 }}>
      <div style={{ fontFamily: fonts.serif, fontSize: 22, color: colors.brandGreen }}>
        {forced ? 'Set your password' : 'Change your password'}
      </div>
      <div style={{ fontSize: 12.5, color: colors.muted, marginTop: 6, lineHeight: 1.6 }}>
        {forced
          ? 'This account is using a one-time password. Choose your own before you continue — nothing else in TISMS will open until you do.'
          : 'You will be signed out everywhere and will need to sign in again with the new password.'}
      </div>

      {done ? (
        <div
          style={{
            marginTop: 18,
            background: '#E3F1E7',
            border: '1px solid #9CC7AB',
            color: colors.infoGreenText,
            borderRadius: radii.smallCard,
            padding: '12px 14px',
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          Password changed. Taking you to the sign-in page — use the new password.
        </div>
      ) : (
        <form onSubmit={submit}>
          <label style={label} htmlFor="cp-current">
            {forced ? 'One-time password' : 'Current password'}
          </label>
          <PasswordInput
            id="cp-current"
            autoComplete="current-password"
            autoFocus
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            style={input}
          />

          <label style={label} htmlFor="cp-new">
            New password
          </label>
          <PasswordInput
            id="cp-new"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            style={input}
          />

          <label style={label} htmlFor="cp-confirm">
            New password again
          </label>
          <PasswordInput
            id="cp-confirm"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            style={{ ...input, borderColor: mismatch ? colors.danger : colors.borderWarm }}
          />
          {mismatch ? (
            <div style={{ fontSize: 11.5, color: colors.danger, marginTop: 5 }}>
              The two entries do not match.
            </div>
          ) : null}

          {/* The rules, stated up front rather than after a rejected attempt. */}
          <div style={{ marginTop: 14, display: 'grid', gap: 4 }}>
            {['At least 6 characters'].map((r) => {
              const failing = rules.includes(r);
              return (
                <div
                  key={r}
                  style={{ fontSize: 11.5, color: next === '' ? colors.faint : failing ? colors.muted : colors.success }}
                >
                  {next !== '' && !failing ? '✓ ' : '· '}
                  {r}
                </div>
              );
            })}
            {rules
              .filter((r) => !r.startsWith('At least'))
              .map((r) => (
                <div key={r} style={{ fontSize: 11.5, color: colors.danger }}>
                  · {r}
                </div>
              ))}
          </div>

          {error ? (
            <div
              style={{
                marginTop: 14,
                background: '#F9E2E0',
                border: `1px solid ${colors.danger}`,
                color: colors.danger,
                borderRadius: radii.smallCard,
                padding: '10px 12px',
                fontSize: 12.5,
              }}
            >
              {error}
            </div>
          ) : null}

          <div style={{ marginTop: 18, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            {!forced ? (
              <Button type="button" variant="outlineNeutral" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            ) : (
              <Button
                type="button"
                variant="outlineNeutral"
                onClick={() => {
                  dispatch(authLoginReset());
                  dispatch(sessionReset());
                  navigate('/', { replace: true });
                }}
              >
                Sign out
              </Button>
            )}
            <Button type="submit" variant="primary" disabled={!canSubmit}>
              {busy ? 'Saving…' : 'Change password'}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );

  /* Forced: stand alone on the page, outside the app shell — there is nothing
     else this account may do yet. */
  if (forced) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: colors.cream,
          padding: 20,
          boxShadow: shadows.card,
        }}
      >
        {body}
      </div>
    );
  }
  return <div style={{ display: 'grid', placeItems: 'center', paddingTop: 40 }}>{body}</div>;
};

export default ChangePasswordPage;
